import { useCallback } from 'react';
import { Modal } from 'antd';
import { FC130 } from '../../../services/c205Api';
import { Mode } from './useFormState';

interface UseFormActionsParams {
  mode: Mode;
  setMode: (m: Mode) => void;
  isEditing: boolean;
  record: FC130 | null;
  currentIdx: number;
  handleSave: (onSuccess: (result: FC130) => void) => Promise<void>;
  handleDelete: () => Promise<boolean>;
  loadList: () => Promise<string[]>;
  setCurrentIdx: (idx: number) => void;
  loadRecordWithNames: (id: string) => Promise<void>;
  applyResult: (result: FC130) => void;
  resetForAdd: () => void;
  revertToRecord: () => void;
  navTo: (idx: number, isEditing: boolean) => void;
}

export function useC205FormActions({
  mode, setMode, isEditing, record, currentIdx,
  handleSave, handleDelete,
  loadList, setCurrentIdx, loadRecordWithNames,
  applyResult, resetForAdd, revertToRecord, navTo,
}: UseFormActionsParams) {

  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(async (result: FC130) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fc130_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      // Enter add mode
      setMode('add');
      resetForAdd();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除退回單 ${record.fc130_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  return {
    handleAddAction,
    handleModifyAction,
    handleDeleteAction,
    handleNavTo,
  };
}
